import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Compass, Loader2, RefreshCw, Shield, AlertTriangle,
  HelpCircle, ChevronRight, DollarSign, Zap, Award,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getAuthToken } from '../../utils/auth';
import Layout from '../../components/Layout';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${getAuthToken()}` } });

const STAGE_LABELS = {
  design: 'Design', prototype: 'Prototype', manufacturing: 'Manufacturing',
  testing: 'Testing', fulfillment: 'Fulfillment',
};

const KindChip = ({ kind }) => (
  <span
    className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${
      kind === 'fact'
        ? 'bg-emerald-500/15 text-emerald-300'
        : 'bg-amber-500/15 text-amber-300'
    }`}
    data-testid="kind-chip"
  >
    {kind === 'fact' ? '✓ Fact' : '~ Assumption'}
  </span>
);

const PathCard = ({ icon: Icon, label, path }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex-1 min-w-[140px]" data-testid={`path-${label.toLowerCase().replace(' ', '-')}`}>
    <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-amber-300 mb-1.5">
      <Icon className="w-3.5 h-3.5" /> {label}
    </div>
    <p className="text-[13px] text-zinc-300 leading-snug">{path?.summary}</p>
    <p className="text-[12px] text-zinc-500 mt-2">
      {path?.est_cost} · {path?.est_time}
    </p>
  </div>
);

const OptionCard = ({ opt }) => (
  <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-3.5" data-testid="scout-option">
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="text-[14px] font-semibold text-white">{opt.name}</p>
        <p className="text-[12px] text-zinc-500">{opt.type === 'person' ? 'Person' : 'Business'} · find on {opt.where}</p>
      </div>
      <KindChip kind={opt.kind} />
    </div>
    <p className="text-[13px] text-zinc-300 mt-2 leading-snug">{opt.why}</p>
    <div className="flex flex-wrap gap-2 mt-2.5 text-[12px]">
      <span className="px-2 py-1 rounded bg-white/[0.04] text-zinc-300">Est. {opt.cost_range}</span>
      <span className="px-2 py-1 rounded bg-white/[0.04] text-zinc-300">Lead {opt.lead_time}</span>
    </div>
    {opt.verify_with && (
      <p className="text-[11px] text-zinc-600 mt-2">To make this a fact: {opt.verify_with}</p>
    )}
  </div>
);

export default function ScoutPage() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async (quiet = false) => {
    try {
      const r = await axios.get(`${API_URL}/api/idea/scout`, auth());
      setData(r.data);
      setSelected((cur) => cur || r.data.projects?.[0]?.id || null);
    } catch { if (!quiet) toast.error('Could not load scout reports'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const report = data?.reports?.[selected];

  useEffect(() => {
    if (report?.status !== 'running') return undefined;
    const t = setInterval(() => load(true), 6000);
    return () => clearInterval(t);
  }, [report?.status, load]);

  const rescout = async () => {
    try {
      await axios.post(`${API_URL}/api/idea/projects/${selected}/scout`, {}, auth());
      toast.success('LEE is scouting — fresh report in about a minute');
      load(true);
    } catch { toast.error('Could not start scout'); }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="scout-page">
        <div>
          <div className="flex items-center gap-2.5">
            <Compass className="w-6 h-6 text-amber-400" />
            <h1 className="text-2xl md:text-3xl font-bold text-white">Invention Scout</h1>
          </div>
          <p className="text-zinc-500 text-sm mt-1.5">
            What it takes, who can build it, what it costs — facts and assumptions clearly separated.
            LEE never contacts anyone without your approval.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-zinc-500 py-10 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading…
          </div>
        ) : !data?.projects?.length ? (
          <p className="text-sm text-zinc-500 text-center py-8" data-testid="scout-empty">
            No inventions yet — describe one on the Inventions page and the Scout Report appears here automatically.
          </p>
        ) : (
          <>
            {/* Idea selector */}
            <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1" data-testid="scout-project-tabs">
              {data.projects.map((p) => (
                <button key={p.id} onClick={() => setSelected(p.id)}
                  className={`px-4 py-2 rounded-full text-[13px] font-semibold whitespace-nowrap transition-colors ${
                    selected === p.id
                      ? 'bg-amber-500 text-black'
                      : 'bg-white/[0.04] text-zinc-400 hover:text-white'}`}
                  data-testid="scout-project-tab">
                  {p.title}
                </button>
              ))}
            </div>

            {!report || report.status === 'error' ? (
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 text-center" data-testid="scout-missing">
                <p className="text-sm text-zinc-400 mb-4">
                  {report?.status === 'error'
                    ? "The last scout run hit a snag — run it again."
                    : 'No Scout Report for this idea yet.'}
                </p>
                <button onClick={rescout}
                  className="px-5 py-2.5 rounded-full bg-amber-500 text-black text-sm font-bold hover:bg-amber-400"
                  data-testid="scout-run-btn">
                  Scout it now
                </button>
              </div>
            ) : report.status === 'running' ? (
              <div className="flex items-center gap-2.5 text-amber-300 text-sm py-10 justify-center" data-testid="scout-running">
                <Loader2 className="w-5 h-5 animate-spin" /> LEE is scouting — mapping stages, people and costs…
              </div>
            ) : (
              <ScoutReport report={report.report} projectId={selected}
                onRescout={rescout} navigate={navigate} />
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

const ScoutReport = ({ report, projectId, onRescout, navigate }) => (
  <div className="space-y-6" data-testid="scout-report">
    {/* Honesty banner */}
    <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.05] p-3.5 text-[12px] text-amber-200/90" data-testid="honesty-banner">
      <strong className="text-amber-300">Facts on file:</strong> {report.honesty?.facts}. {report.honesty?.note}
    </div>

    {/* Brief */}
    <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-5" data-testid="scout-brief">
      <h2 className="text-base font-bold text-white mb-3">Product Brief</h2>
      <div className="space-y-2 text-[14px] text-zinc-300">
        <p><span className="text-zinc-500">What it is:</span> {report.brief?.what}</p>
        <p><span className="text-zinc-500">Who it's for:</span> {report.brief?.who_for}</p>
        <p><span className="text-zinc-500">Done looks like:</span> {report.brief?.done_looks_like}</p>
      </div>
    </section>

    {/* Three paths */}
    {report.paths && (
      <section data-testid="scout-paths">
        <h2 className="text-base font-bold text-white mb-3">Three ways to play it</h2>
        <div className="flex flex-col sm:flex-row gap-3">
          <PathCard icon={DollarSign} label="Cheapest" path={report.paths.cheapest} />
          <PathCard icon={Zap} label="Fastest" path={report.paths.fastest} />
          <PathCard icon={Award} label="Best Quality" path={report.paths.best_quality} />
        </div>
      </section>
    )}

    {/* Stages */}
    <section className="space-y-5" data-testid="scout-stages">
      <h2 className="text-base font-bold text-white">Stage by stage — who can do it</h2>
      {(report.stages || []).map((s, i) => (
        <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4" data-testid={`scout-stage-${s.stage}`}>
          <div className="flex items-center gap-2 mb-1">
            <span className="w-6 h-6 rounded-full bg-amber-500/15 text-amber-300 text-[12px] font-bold flex items-center justify-center">{i + 1}</span>
            <h3 className="text-[15px] font-bold text-white">{STAGE_LABELS[s.stage] || s.stage}</h3>
          </div>
          <p className="text-[13px] text-zinc-400 mb-3">{s.whats_needed}</p>
          <div className="grid gap-2.5 sm:grid-cols-2">
            {(s.options || []).map((o, j) => <OptionCard key={j} opt={o} />)}
          </div>
        </div>
      ))}
    </section>

    {/* Risks */}
    <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-4" data-testid="scout-risks">
      <h2 className="text-base font-bold text-white mb-3 flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-red-400" /> What could go wrong
      </h2>
      <div className="space-y-2.5">
        {(report.risks || []).map((r, i) => (
          <div key={i} className="text-[13px]" data-testid="scout-risk">
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase mr-2 ${
              r.severity === 'high' ? 'bg-red-500/15 text-red-300'
                : r.severity === 'medium' ? 'bg-amber-500/15 text-amber-300'
                  : 'bg-white/[0.06] text-zinc-400'}`}>{r.severity}</span>
            <span className="text-zinc-300">{r.risk}</span>
            <p className="text-zinc-600 text-[12px] mt-0.5 ml-0.5">Worst case: {r.worst_case}</p>
          </div>
        ))}
      </div>
    </section>

    {/* Questions */}
    <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-4" data-testid="scout-questions">
      <h2 className="text-base font-bold text-white mb-3 flex items-center gap-2">
        <HelpCircle className="w-4 h-4 text-blue-400" /> Questions to ask before committing
      </h2>
      <div className="space-y-2">
        {(report.questions || []).map((q, i) => (
          <div key={i} className="text-[13px] text-zinc-300" data-testid="scout-question">
            <span className="text-zinc-500">{q.for}:</span> "{q.ask}"
          </div>
        ))}
      </div>
    </section>

    {/* Approval wall + actions */}
    <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-3.5 flex items-start gap-2.5" data-testid="approval-note">
      <Shield className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
      <p className="text-[12px] text-emerald-200/90">{report.approval_note}</p>
    </div>
    <div className="flex flex-wrap items-center gap-3 pb-4">
      <button onClick={() => navigate(`/inventions/${projectId}/builders`)}
        className="flex items-center gap-1.5 px-6 py-3 rounded-full bg-amber-500 text-black text-[14px] font-bold hover:bg-amber-400"
        data-testid="scout-find-candidates-btn">
        Find real candidates <ChevronRight className="w-4 h-4" />
      </button>
      <button onClick={onRescout}
        className="flex items-center gap-1.5 text-[13px] text-zinc-500 hover:text-zinc-300"
        data-testid="scout-refresh-btn">
        <RefreshCw className="w-3.5 h-3.5" /> Re-scout
      </button>
    </div>
  </div>
);
